import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchImportDeletionStats } from 'api/imports';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Import } from 'types/domain';

const useDeletionStats = (importRecord: Import) => {
    const { accountId } = useCurrentAccount();

    const {
        data: deletionStats = {
            contacts: 0,
            ignored_contacts: 0,
            transactions: 0,
            activities: 0,
            soft_credits: 0,
        },
    } = useSuspenseQuery({
        queryKey: accountQueryKeys.importDeletionStats(accountId, importRecord.id, 'predelete'),
        queryFn: () =>
            fetchImportDeletionStats(accountId, importRecord.id, {
                type: importRecord.resource,
                includeContacts:
                    importRecord.resource === 'transactions' ||
                    importRecord.resource === 'soft_credits',
                includeTransactions:
                    importRecord.resource === 'contacts' || importRecord.resource === 'companies',
                deletionType: 'predelete',
            }),
        select: data => data?.data,
    });

    const hasContacts = deletionStats.contacts !== 0;
    const hasTransactions = deletionStats.transactions !== 0;
    const hasIgnoredContacts = deletionStats.ignored_contacts !== 0;
    const hasActivities = deletionStats.activities !== 0;
    const hasSoftCredits = deletionStats.soft_credits !== 0;
    const hasNone =
        !hasContacts &&
        !hasTransactions &&
        !hasIgnoredContacts &&
        !hasActivities &&
        !hasSoftCredits;
    const hasAny =
        hasContacts || hasTransactions || hasIgnoredContacts || hasActivities || hasSoftCredits;

    return {
        deletionStats,
        hasContacts,
        hasTransactions,
        hasIgnoredContacts,
        hasActivities,
        hasSoftCredits,
        hasNone,
        hasAny,
    };
};

export { useDeletionStats };
